import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, useMediaQuery, GlobalStyles } from '@mui/material';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyDocumentTheme(isDark: boolean) {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  if (isDark) {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
  root.style.colorScheme = isDark ? 'dark' : 'light';
  requestAnimationFrame(() => {
    root.classList.remove('theme-switching');
  });
}

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useAppTheme must be used within AppThemeProvider');
  return context;
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('cwy-theme-mode');
    return (saved as ThemeMode) || 'system';
  });

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const isDark = mode === 'system' ? prefersDarkMode : mode === 'dark';

  const setModeAndSync = useCallback((nextMode: ThemeMode) => {
    flushSync(() => {
      setMode(nextMode);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('cwy-theme-mode', mode);
  }, [mode]);

  useLayoutEffect(() => {
    applyDocumentTheme(isDark);
  }, [isDark]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          primary: {
            main: '#CC1B1B',
            light: isDark ? '#FF4B3E' : '#D93A2E',
            dark: '#B81717',
          },
          secondary: {
            main: isDark ? '#33C47E' : '#1E9D63',
            light: isDark ? '#55D49E' : '#2FB875',
            dark: isDark ? '#2AA067' : '#167B4D',
          },
          background: {
            default: isDark ? '#000000' : '#F7F6F4',
            paper: isDark ? '#121212' : '#ffffff',
          },
          text: {
            primary: isDark ? '#ffffff' : '#15161A',
            secondary: isDark ? '#a0a0a0' : '#5C5D63',
          },
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontFamily: '"Sora", sans-serif', fontWeight: 700, color: isDark ? '#ffffff' : '#15161A', letterSpacing: '-0.02em' },
          h2: { fontFamily: '"Sora", sans-serif', fontWeight: 700, color: isDark ? '#ffffff' : '#15161A', letterSpacing: '-0.015em' },
          h3: { fontFamily: '"Sora", sans-serif', fontWeight: 700, color: isDark ? '#ffffff' : '#15161A' },
          h4: { fontFamily: '"Sora", sans-serif', fontWeight: 600, color: isDark ? '#ffffff' : '#15161A' },
          h5: { fontFamily: '"Sora", sans-serif', fontWeight: 700, color: isDark ? '#ffffff' : '#15161A' },
          h6: { fontFamily: '"Sora", sans-serif', fontWeight: 600, color: isDark ? '#ffffff' : '#15161A' },
          body1: { fontSize: '0.9375rem' },
          body2: { fontSize: '0.875rem' },
          caption: { fontSize: '0.75rem' },
          button: { fontWeight: 600, fontSize: '0.875rem' },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                boxShadow: 'none',
              },
              contained: {
                boxShadow: isDark ? '0 0 0 1px rgba(255,75,62,0.25), 0 8px 24px -8px rgba(204,27,27,0.45)' : 'none',
                '&:hover': { boxShadow: isDark ? '0 0 0 1px rgba(255,75,62,0.4), 0 10px 28px -6px rgba(204,27,27,0.55)' : 'none' },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 14,
                boxShadow: 'none',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(20,21,26,0.08)',
                backgroundColor: isDark ? '#121212' : 'rgba(255,255,255,0.55)',
                backdropFilter: isDark ? 'none' : 'blur(18px) saturate(150%)',
                WebkitBackdropFilter: isDark ? 'none' : 'blur(18px) saturate(150%)',
                transition: 'transform .18s ease, border-color .18s ease, box-shadow .18s ease',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: { 
                borderRadius: 14,
                backgroundColor: isDark ? '#121212' : 'rgba(255,255,255,0.55)',
                backdropFilter: isDark ? 'none' : 'blur(18px) saturate(150%)',
                WebkitBackdropFilter: isDark ? 'none' : 'blur(18px) saturate(150%)',
                backgroundImage: 'none',
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 600,
                fontSize: '0.7rem',
              },
            },
          },
        },
        shape: { borderRadius: 8 },
      }),
    [isDark]
  );

  return (
    <ThemeContext.Provider value={{ mode, setMode: setModeAndSync, isDark }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            'html.theme-switching, html.theme-switching *': {
              transition: 'none !important',
            },
            ':root': {
              '--cwy-pageBg': '#F7F6F4',
              '--cwy-bg': '#F7F6F4',
              '--cwy-ambient1': 'rgba(204,27,27,0.05)',
              '--cwy-ambient2': 'rgba(51,196,126,0.05)',
              '--cwy-glassBg': 'rgba(255,255,255,0.55)',
              '--cwy-glassBgRaised': 'rgba(255,255,255,0.8)',
              '--cwy-cardBg': 'rgba(255,255,255,0.55)',
              '--cwy-border': 'rgba(20,21,26,0.08)',
              '--cwy-borderSoft': 'rgba(20,21,26,0.06)',
              '--cwy-glassBlur': '18px',
              '--cwy-redCore': '#CC1B1B',
              '--cwy-redWash': 'rgba(204,27,27,0.08)',
              '--cwy-textPrimary': '#15161A',
              '--cwy-textSecondary': '#5C5D63',
              '--cwy-textFaint': '#8B8C92',
              '--cwy-verified': '#1E9D63',
              '--cwy-navBg': '#F7F6F4',
              '--cwy-chatOutgoingBg': '#d2e3fc',
              '--cwy-chatOutgoingText': '#0f0f0f',
              '--cwy-chatOutgoingMuted': 'rgba(21, 22, 26, 0.6)',

              /* Concept Theme Variables - Light mode */
              '--canvas': '#F7F6F4',
              '--ambient-1': 'rgba(204,27,27,0.05)',
              '--ambient-2': 'rgba(51,196,126,0.05)',
              '--glass-bg': 'rgba(255,255,255,0.55)',
              '--glass-bg-raised': 'rgba(255,255,255,0.8)',
              '--glass-border': 'rgba(20,21,26,0.08)',
              '--glass-blur': '18px',
              '--line': 'rgba(20,21,26,0.10)',
              '--line-soft': 'rgba(20,21,26,0.06)',
              '--red-core': '#CC1B1B',
              '--red-bright': '#D93A2E',
              '--red-wash': 'rgba(204,27,27,0.08)',
              '--ink': '#15161A',
              '--ink-dim': '#5C5D63',
              '--ink-faint': '#8B8C92',
              '--verified': '#1E9D63',
              '--verified-wash': 'rgba(30,157,99,0.10)',
              '--nav-bg': '#F7F6F4',
              '--img-filter': 'saturate(1.05) brightness(1)',
            },
            '[data-theme="dark"]': {
              '--cwy-pageBg': '#000000',
              '--cwy-bg': '#000000',
              '--cwy-ambient1': 'rgba(0,0,0,0)',
              '--cwy-ambient2': 'rgba(0,0,0,0)',
              '--cwy-glassBg': '#121212',
              '--cwy-glassBgRaised': '#1c1c1e',
              '--cwy-cardBg': '#121212',
              '--cwy-border': 'rgba(255,255,255,0.08)',
              '--cwy-borderSoft': 'rgba(255,255,255,0.05)',
              '--cwy-glassBlur': '0px',
              '--cwy-redCore': '#CC1B1B',
              '--cwy-redWash': 'rgba(204,27,27,0.14)',
              '--cwy-textPrimary': '#ffffff',
              '--cwy-textSecondary': '#a0a0a0',
              '--cwy-textFaint': '#606060',
              '--cwy-verified': '#33C47E',
              '--cwy-navBg': '#000000',
              '--cwy-chatOutgoingBg': '#005c4b',
              '--cwy-chatOutgoingText': '#ffffff',
              '--cwy-chatOutgoingMuted': 'rgba(255, 255, 255, 0.7)',

              /* Concept Theme Variables - Dark mode */
              '--canvas': '#000000',
              '--ambient-1': 'rgba(0,0,0,0)',
              '--ambient-2': 'rgba(0,0,0,0)',
              '--glass-bg': '#121212',
              '--glass-bg-raised': '#1c1c1e',
              '--glass-border': 'rgba(255,255,255,0.08)',
              '--glass-blur': '0px',
              '--line': 'rgba(255,255,255,0.08)',
              '--line-soft': 'rgba(255,255,255,0.05)',
              '--red-core': '#CC1B1B',
              '--red-bright': '#FF4B3E',
              '--red-wash': 'rgba(204,27,27,0.14)',
              '--ink': '#ffffff',
              '--ink-dim': '#a0a0a0',
              '--ink-faint': '#606060',
              '--verified': '#33C47E',
              '--verified-wash': 'rgba(51,196,126,0.12)',
              '--nav-bg': '#000000',
              '--img-filter': 'saturate(0.9) brightness(0.9)',
            },
          }}
        />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
